import { useLogout } from '@/composables/useLogout'
import { useToast } from '@/composables/useToast'
import { ApiErrorHandler } from '@/lib/error-handler'
import { ValidationMessages } from '@/utils/validation'
import { extractData, syncQueryData } from '@/lib/vue-query'
import { UserInfoSchema } from '@/types/api'
import { getUserInfo } from '@/api/user'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

// User info interface matching our application needs
export interface UserInfo {
  id: number
  username: string
  email: string
  has2FA: boolean
}

// User registration data interface
export interface RegisterData {
  username: string
  email: string
  password: string
}

export const useAuthStore = defineStore('auth', () => {
  const { showError } = useToast()
  const { shouldPreventAutoAuth } = useLogout()

  const user = ref<UserInfo | null>(null)
  const isLoggedIn = ref(false)
  const has2fa = ref(false)
  const is2faVerified = ref(false)
  const sessionReady = ref(false)
  const loading = ref(false)

  // Track if user had an active session before (for proper error messages)
  const hadActiveSession = ref(false)

  // Auth check promise to prevent concurrent calls
  let authCheckPromise: Promise<boolean> | null = null

  /**
   * Check if user had an active session before (stored in localStorage)
   */
  function hasHadActiveSession(): boolean {
    // First check reactive ref for immediate updates
    if (hadActiveSession.value) {
      return true
    }

    // Fallback to localStorage
    try {
      return localStorage.getItem('had_active_session') === 'true'
    } catch {
      return false
    }
  }

  /**
   * Mark that user has an active session
   */
  function markActiveSession() {
    hadActiveSession.value = true
    try {
      localStorage.setItem('had_active_session', 'true')
    } catch (error) {
      console.warn('Failed to save session state:', error)
    }
  }

  /**
   * Clear active session marker
   */
  function clearActiveSessionMarker() {
    hadActiveSession.value = false
    try {
      localStorage.removeItem('had_active_session')
    } catch (error) {
      console.warn('Failed to clear session state:', error)
    }
  }

  const isAuthenticated = computed(() => isLoggedIn.value && (!has2fa.value || is2faVerified.value))
  const needsToVerify2FA = computed(() => isLoggedIn.value && has2fa.value && !is2faVerified.value)

  function setLoggedIn(value: boolean) {
    isLoggedIn.value = value
    if (!value) {
      user.value = null
    }
  }

  function setHas2fa(value: boolean) {
    has2fa.value = value
  }

  function setIs2faVerified(value: boolean) {
    is2faVerified.value = value
  }

  function setUser(newUser: UserInfo | null) {
    user.value = newUser
    if (newUser) {
      has2fa.value = newUser.has2FA
    }
  }

  /**
   * Fetch user info for session management
   */
  async function fetchUser() {
    loading.value = true

    try {
      const response = await getUserInfo()
      const data = extractData(response)
      const userInfo = UserInfoSchema.parse(data)

      user.value = {
        id: userInfo.user_id,
        username: userInfo.user_name,
        email: userInfo.email,
        has2FA: userInfo.is_enabled_2fa
      }

      has2fa.value = userInfo.is_enabled_2fa
      isLoggedIn.value = true
      is2faVerified.value = true
      sessionReady.value = true

      // Mark that user has an active session
      markActiveSession()

      return true
    } catch (err: unknown) {
      console.error('AuthStore: fetchUser error', err)

      const errorHandler = new ApiErrorHandler(err)
      if (errorHandler.isAuthError()) {
        resetAuthState()
        sessionReady.value = true

        // Only show session expired error if user had an active session before
        // For new users or first-time visitors, don't show this error
        if (hasHadActiveSession()) {
          showError(ValidationMessages.ERROR_SESSION_EXPIRED)
        }

        return false
      }

      resetAuthState()
      sessionReady.value = true
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * Check if user has active session
   */
  async function checkAuth() {
    if (authCheckPromise) {
      return authCheckPromise
    }

    if (shouldPreventAutoAuth()) {
      sessionReady.value = true
      return false
    }

    if (sessionReady.value && !isLoggedIn.value) {
      return false
    }

    if (sessionReady.value && isLoggedIn.value) {
      return true
    }

    authCheckPromise = (async () => {
      try {
        const success = await fetchUser()
        if (!success) {
          sessionReady.value = true
        }
        return success
      } catch (err) {
        sessionReady.value = true
        return false
      } finally {
        authCheckPromise = null
      }
    })()

    return authCheckPromise
  }

  /**
   * Reset authentication state
   */
  function resetAuthState() {
    user.value = null
    isLoggedIn.value = false
    has2fa.value = false
    is2faVerified.value = false

    // Note: We don't clear hadActiveSession here as it's used to determine
    // whether to show session expired messages. It should be cleared only on logout.
  }

  /**
   * Sync with Vue Query cache
   */
  function syncWithQueryData() {
    syncQueryData(['user', 'info'], user as any, null)
  }

  return {
    user,
    isLoggedIn,
    has2fa,
    is2faVerified,
    sessionReady,
    loading,
    hadActiveSession,

    isAuthenticated,
    needsToVerify2FA,

    setLoggedIn,
    setHas2fa,
    setIs2faVerified,
    setUser,

    fetchUser,
    checkAuth,
    resetAuthState,
    syncWithQueryData,

    // Session tracking functions
    hasHadActiveSession,
    markActiveSession,
    clearActiveSessionMarker
  }
})
